/**
 * Chrome 142+ Local Network Access treats loopback, RFC1918, .local, and
 * IPv4-mapped numeric hosts (e.g. `sitescope-attachment://9` → host `9` →
 * `0.0.0.9`) as local devices. A public origin (`itssecure.vertiv.com`) that
 * puts those URLs on `<img src>` prompts “access devices on your local network”.
 */

const CUSTOM_ATTACHMENT_SCHEME = /^sitescope-attachment(?:-pending)?:/i;

function parseIPv4Octets(
  host: string,
): readonly [number, number, number, number] | null {
  if (/^\d+$/.test(host)) {
    const n = Number(host);
    if (!Number.isSafeInteger(n) || n < 0 || n > 4294967295) {
      return null;
    }
    return [
      Math.floor(n / 16777216) % 256,
      Math.floor(n / 65536) % 256,
      Math.floor(n / 256) % 256,
      n % 256,
    ];
  }
  const dotted = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!dotted) {
    return null;
  }
  const octets = dotted.slice(1, 5).map(Number);
  const [a, b, c, d] = octets;
  if (
    a === undefined ||
    b === undefined ||
    c === undefined ||
    d === undefined ||
    octets.some(part => !Number.isInteger(part) || part > 255)
  ) {
    return null;
  }
  return [a, b, c, d];
}

function isPrivateOrLoopbackIPv4(a: number, b: number): boolean {
  if (a === 0 || a === 10 || a === 127) {
    return true;
  }
  if (a === 169 && b === 254) {
    return true;
  }
  if (a === 172 && b >= 16 && b <= 31) {
    return true;
  }
  if (a === 192 && b === 168) {
    return true;
  }
  return false;
}

export function isLocalNetworkImgSrc(src: string): boolean {
  const trimmed = src.trim();
  if (!trimmed) {
    return false;
  }
  if (CUSTOM_ATTACHMENT_SCHEME.test(trimmed)) {
    return true;
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol === 'blob:' || url.protocol === 'data:') {
      return false;
    }
    if (url.protocol === 'file:') {
      return true;
    }
    const host = url.hostname.replace(/^\[|\]$/g, '').toLowerCase();
    if (host === 'localhost' || host === '::1' || host.endsWith('.local')) {
      return true;
    }
    const ipv4 = parseIPv4Octets(host);
    if (ipv4 !== null) {
      const [a, b] = ipv4;
      return isPrivateOrLoopbackIPv4(a, b);
    }
    return false;
  } catch {
    return false;
  }
}

/** Live `<img src>` the browser may fetch without a local-network prompt. */
export function isBrowserSafeInlineImageSrc(src: string): boolean {
  return /^(data:image\/|blob:|https:)/i.test(src.trim());
}
